import React from "react";

import { Entity, FieldHandler } from "@thewebops/admin-panel";
import { Switch } from "antd";

export default new Entity({
  name: "user",
  pluralName: "users",
  pathName: "users",
  endpoint: "/admin/users",
  serverName: "user",
  serverPluralName: "users",
  displayName: "Users",
  allowCreate: true,
  allowEdit: true,
  allowDelete: true,
  showRowActions: true,
  showSearch: true,
  showFiltersInDrawer: true,
  titleField: "name",
  fields: [
    {
      identifier: "id",
      label: "ID",
      accessor: "id",
      inList: true,
      usedToShow: true,
      hideInCreate: true,
      hideInEdit: true,
    },
    {
      identifier: "name",
      label: { en: "Name", ar: "الاسم" },
      accessor: "name",
      inList: true,
      type: "string",
      required: true,
    },
    {
      identifier: "email",
      label: { en: "Email", ar: "بريد الالكتروني" },
      accessor: "email",
      inList: true,
      type: "email",
      disabled: (action) => action === "edit",
      required: true,
      usedToShow: true,
    },
    {
      identifier: "password",
      label: "Password",
      accessor: "password",
      hideInShow: true,
      type: "password",
      valuesRejectedOnSubmit: [""],
    },
    {
      identifier: "password_confirmation",
      label: "Password Confirmation",
      accessor: "password_confirmation",
      hideInShow: true,
      type: "password",
      valuesRejectedOnSubmit: [""],
    },
    {
      identifier: "active",
      label: "Active",
      accessor: (dataObject) => (dataObject?.active ? true : false),
      hideInCreate: true,
      inList: true,
      type: "select",
      options: [
        {
          value: true,
          label: { en: "Active", ar: "نشط" },
        },
        {
          value: false,
          label: { en: "Inactive", ar: "غير نشط" },
        },
      ],
    },
    {
      identifier: "restrict_access",
      label: "Restrict Access",
      accessor: (dataObject) => (dataObject?.restrict_access ? true : false),
      inList: true,
      type: "select",
      required: true,
      defaultValue: false,
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
    },
    {
      identifier: "whitelisted_ips",
      label: "Whitelisted IPs",
      accessor: "whitelisted_ips",
      inList: true,
      type: "string",
      dependsOn: "restrict_access",
      dependsOnValue: true,
    },
    {
      identifier: "api_access",
      label: "API Access",
      accessor: "api_access",
      type: "checkbox",
      checkboxConfig: {
        format: "array",
      },
      //TODO: Remove this custom component when the checkbox is fixed
      component: ({ value, field }) => {
        if (!value) return "_";

        const keyValueOptions = field.options.reduce((accum, option) => {
          accum[option.value] = option.label;
          return accum;
        }, {});
        const displayValues = value.map((item) => keyValueOptions[item]).join(", ");
        return <span>{displayValues}</span>;
      },
      options: [
        { value: "gi", label: "GI" },
        { value: "grp", label: "GRP" },
      ],
    },

    {
      identifier: "api_roles",
      label: "API Roles",
      accessor: "api_roles",
      type: "checkbox",
      checkboxConfig: {
        format: "array",
      },
      dependee: true,
      persistDependeeValues: true,
      //TODO: Remove this custom component when the checkbox is fixed
      component: ({ value, field }) => {
        if (!value) return "_";

        const keyValueOptions = field.options.reduce((accum, option) => {
          accum[option.value] = option.label;
          return accum;
        }, {});
        const displayValues = value.map((item) => keyValueOptions[item]).join(", ");
        return <span>{displayValues}</span>;
      },
      options: [
        { value: "gi_user", label: "GI User" },
        { value: "gs1_client", label: "GS1 Client" },
        { value: "eda_user", label: "EDA User" },
      ],
    },
    {
      identifier: "eda_checkgtin_tax_id_required",
      label: "EDA CheckGTIN Tax ID Required?",
      accessor: (dataObject) => (dataObject?.eda_checkgtin_tax_id_required ? true : false),
      type: "select",
      dependsOn: "api_roles",
      dependsOnValue: "eda_user",
      defaultValue: true,
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
    },
    // THe below field is duplicated as a aresult of dependeee fields issue resulting the field not rendered in show
    {
      identifier: "eda_checkgtin_tax_id_required",
      label: "EDA CheckGTIN Tax ID Required?",
      accessor: (dataObject) => (dataObject?.eda_checkgtin_tax_id_required ? 'Yes' : 'No'),
      type: "string",
      hideInCreate: true,
      hideInEdit: true,
    },
    {
      identifier: "sales_channel_users_attributes",
      label: { en: "Sales Channels", ar: "اختصار الشركة العالمي" },
      lgCol: 12,
      mdCol: 12,
      accessor: "sales_channel_users_attributes",
      type: "transferList",
      transferListConfig: {
        accessor: "sales_channel_users",
        showSearch: true,
      },
      optionsEntity: {
        name: "salesChannel",
        valueField: "id",
        labelField: "channel_name",
        valueLabel: "sales_channel_id",
        displayAsLink: true,
        required: true,
        filterParams: [
          {
            name: "user_id",
            value: ["params", "id"],
            type: "both",
          },
        ],
      },
    },
    {
      identifier: "environment",
      label: "Environment",
      accessor: (dataObject) => (dataObject?.environment ? dataObject?.environment : "_"),
      inList: true,
      type: "string",
      hideInCreate: true,
      hideInEdit: true,
    },
    {
      identifier: "sync_enabled",
      label: "Sync Enabled",
      accessor: "sync_enabled",
      inList: true,
      type: "select",
      options: [
        { label: "True", value: true },
        { label: "False", value: false },
      ],
      hideInCreate: true,
      hideInEdit: true,
    },
    {
      identifier: "detailed_grp_errors_enabled",
      label: "Detailed GRP Error",
      accessor: "detailed_grp_errors_enabled",
      dependsOn: "api_access",
      dependsOnValue: "grp",
      type: "radio",
      options: [
        {
          value: true,
          label: { en: "Enable", ar: "تفعيل" },
        },
        {
          value: false,
          label: { en: "Disable", ar: "تعطيل" },
        },
      ],
      lgCol: 2,
      mdCol: 2,
      hideInShow: true,
      hideInEdit: false,
      hideInCreate: false,
    },
    {
      label: "Detailed GRP Error",
      accessor: "detailed_grp_errors_enabled",
      type: "radio",
      options: [
        {
          value: true,
          label: { en: "Enable", ar: "تفعيل" },
        },
        {
          value: false,
          label: { en: "Disable", ar: "تعطيل" },
        },
      ],
      lgCol: 2,
      mdCol: 2,
      defaultValue: false,
      hideInShow: false,
      hideInEdit: true,
      hideInCreate: true,
    },
    {
      identifier: "extended_gcp_data_enabled",
      label: "Extended GCP Information",
      accessor: "extended_gcp_data_enabled",
      type: "radio",
      options: [
        {
          value: true,
          label: { en: "Enable", ar: "تفعيل" },
        },
        {
          value: false,
          label: { en: "Disable", ar: "تعطيل" },
        },
      ],
      lgCol: 2,
      mdCol: 2,
      defaultValue: false,
    },
    {
      identifier: "dashboard_access_enabled",
      label: "Dashboard Access Enabled",
      accessor: "dashboard_access_enabled",
      type: "radio",
      options: [
        {
          value: true,
          label: { en: "Enable", ar: "تفعيل" },
        },
        {
          value: false,
          label: { en: "Disable", ar: "تعطيل" },
        },
      ],
      lgCol: 2,
      mdCol: 2,
      defaultValue: false,
    },
    {
      identifier: "organization_id",
      label: "Organization",
      accessor: (dataObject) => dataObject?.organization?.name,
      formAccessor: (dataObject) => dataObject?.organization?.id,
      type: "reference",
      inList: true,
      hideInEdit: true,
      hideInCreate: true,
      optionsEntity: {
        name: "organization",
        labelField: "name",
        valueField: "id",
        displayAsLink: true,
      },
      hideInCreate: true,
      hideInEdit: true,
    },
    {
      identifier: "user_api_attributes",
      label: "User Api Attributes",
      accessor: "user_assigned_api_attributes",
      hideInCreate: true,
      hideInEdit: true,
      type: "table",
      lgCol: 12,
      mdCol: 12,
      fields: [
        {
          identifier: "user_api_attribute_name",
          label: "Attribute Name",
          type: "string",
          inList: true,
          accessor: "user_api_attribute_name",
        },
        {
          identifier: "user_api_attribute_data_type",
          label: "Attribute Data Type",
          type: "string",
          inList: true,
          accessor: "user_api_attribute_data_type",
        },
        {
          identifier: "required",
          label: "Required",
          type: "string",
          inList: true,
          accessor: "required",
        },
      ],
      tableConfig: {
        name: "user",
      },
    },
    {
      identifier: "user_assigned_api_attributes_attributes",
      label: "User Attributes",
      accessor: "user_assigned_api_attributes",
      hideInShow: true,
      type: "dynamic",
      fields: [
        {
          identifier: "id",
          type: "string",
          accessor: "id",
          hideInEdit: true,
          hideInCreate: true,
        },
        {
          identifier: "user_api_attribute_id",
          label: { en: "Attribute Name", ar: "اسم السمة" },
          accessor: "user_api_attribute_name",
          formAccessor: "user_api_attribute_id",
          type: "reference",
          required: true,
          optionsEntity: {
            name: "userApiAttribute",
            labelField: "name",
            valueField: "id",
          },
        },
        {
          identifier: "required",
          label: "Required",
          accessor: "required",
          type: "radio",
          required: true,
          options: [
            { label: "Yes", value: true },
            { label: "No", value: false },
          ],
          lgCol: 2,
          mdCol: 2,
        },
      ],
    },
  ],
  filters: [
    {
      label: "Organization",
      name: "organization_id",
      type: "select",
      selectConfig: {
        optionsType: "dynamic",
        isSearchable: true,
        optionsEntity: {
          name: "organization",
          labelField: "name",
          valueField: "id",
        },
      },
    },
    {
      label: "Restricted",
      name: "restrict_access",
      type: "select",
      selectConfig: {
        optionsType: "static",
        optionsArray: [
          { label: "Yes", value: true },
          { label: "No", value: false },
        ],
      },
    },
    {
      label: "Sync Enabled",
      name: "sync_enabled",
      type: "select",
      selectConfig: {
        optionsType: "static",
        optionsArray: [
          { label: "True", value: true },
          { label: "False", value: false },
        ],
      },
    },
    {
      label: "Active",
      name: "active",
      type: "select",
      selectConfig: {
        optionsType: "static",
        optionsArray: [
          { label: "Active", value: true },
          { label: "Inactive", value: false },
        ],
      },
    },
    {
      label: "Environment",
      name: "environment",
      type: "select",
      selectConfig: {
        optionsType: "static",
        optionsArray: [
          { label: "Live", value: "live" },
          { label: "Test", value: "test" },
          { label: "Development", value: "development" },
        ],
      },
    },
  ],
});
